import React, { useCallback, useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import "./registration.scss";
import { useDispatch, useSelector } from "react-redux";
import {
  createBMCProfile,
  resetBMCProfileData,
  setIsEditProfilePopupOpen,
  setProfileData,
  updateBMCProfile,
  setOTPPopupOpen,
  validateCompanyNameUnique,
  validateEmailUnique,
  validatePhoneNumberUnique,
  setIsEmailVerified,
  setIsSmSVerified,
  generateOTP,
} from "src/redux/slices/bMCProfileSlice";
import { useNavigate } from "react-router";
import _ from "lodash";
import { OTPType, RoutePaths } from "src/resources/enum";
import OTP from "../OTP/OTP";
import { useTranslation } from "react-i18next";
import { Button } from "primereact/button";
import {
  ALLOWED_FILES_EXTENSION,
  DELAY_TIME,
  MAX_FILE_SIZE,
} from "src/resources/constant";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  calculateFileSizeFromBase64,
  convertIsoStringToDate,
  getFieldName,
  isNullOrWhiteSpace,
  scrollToTopWithClass,
} from "src/utils/helpers";
import { IEmirate } from "src/redux/interfaces/ILookup_1";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { registrationInitialState } from "src/state/registrationInitialState";
import { registrationSchema } from "src/schemas/registrationSchema";
import CustomInputNumber from "../InputNumber/InputNumber";
import FileUploader from "../FileUploader/FileUploader";
import { FileUpload } from "primereact/fileupload";
import { getCategoryOptions } from "src/redux/slices/lookupSlice";
import { Checkbox } from "primereact/checkbox";
import TermsAndConditionsPopup from "../TermsAndConditions/TermsAndConditionsPopup";
import { onHideCalendar, onShowCalendar } from "src/utils/common";

interface RegistrationFormProps {
  isViewMode?: boolean;
  isEditMode?: boolean;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  isViewMode = false,
  isEditMode = false,
}) => {
  const fileUploadRef = useRef<FileUpload>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "user", "registration"]);

  const civilDefenseDateRef = useRef<Calendar | null>(null);
  const tradeLicenseDateRef = useRef<Calendar | null>(null);

  const categoryOptions = useSelector(
    (state: any) => state.lookup.categoryOptions
  );
  const emirateOptions = useSelector((state: any) =>
    state.lookup.emiratesOptions.filter(
      (em: IEmirate) => em.emirateCode === "AUH"
    )
  );
  const citiesOptions = useSelector((state: any) => state.lookup.citiesOptions);
  const language = useSelector((state: any) => state.lookup.language);

  const { BMCData } = useSelector((state: any) => state.bMCProfile);
  const { isEmailVerified, isSmSVerified } = useSelector(
    (state: any) => state.bMCProfile
  );

  const [BMCEditData, setBMCEditData] = useState(_.cloneDeep(BMCData));

  const [isEmailChanged, setIsEmailChanged] = useState(false);
  const [isPhoneNumberChanged, setIsPhoneNumberChanged] = useState(false);

  const [isCompanyNameUnique, setIsCompanyNameUnique] = useState(true);
  const [isEmailUnique, setIsEmailUnique] = useState(true);
  const [isPhoneNumberUnique, setIsPhoneNumberUnique] = useState(true);
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
  const [isSubmitButtonClicked, setIsSubmitButtonClicked] =
    useState<any>(false);

  const [errors, setErrors] = useState(registrationInitialState);
  const [termsAndConditionsFlag, setTermsAndConditionsFlag] = useState(false);
  const [isTermsAndConditionsPopupOpen, setIsTermsAndConditionsPopupOpen] =
    useState(false);

  const isCompanyNameUniqueValidation = async () => {
    const resData = await dispatch(
      validateCompanyNameUnique(BMCEditData.name) as any
    );
    setIsCompanyNameUnique(resData.data);
    return resData.data;
  };
  const scrollUp = useCallback(() => {
    const className = "invalid-input";
    const parentClass = isEditMode ? "p-dialog-content" : null;
    scrollToTopWithClass(className, parentClass);
  }, [isEditMode]);

  const isEmailUniqueValidation = async () => {
    const resData = await dispatch(
      validateEmailUnique(BMCEditData.email) as any
    );
    setIsEmailUnique(resData.data);
    return resData.data;
  };
  const isPhoneNumberUniqueValidation = async (): Promise<boolean> => {
    const resData = await dispatch(
      validatePhoneNumberUnique(BMCEditData.phoneNumber) as any
    );
    setIsPhoneNumberUnique(resData.data);
    return resData.data;
  };
  const validateRegistrationForm = useCallback(
    async (data: any) => {
      const { error } = registrationSchema(data, isEditMode).validate(data, {
        abortEarly: false,
      });
      if (error) {
        const newErrors: any = {};
        error.details.forEach((err: any) => {
          newErrors[err.context?.key] = err.message;
        });
        setErrors(newErrors);
        return false;
      }
      setErrors(registrationInitialState);
      return true;
    },
    [isCompanyNameUnique, isEditMode, isEmailUnique, isPhoneNumberUnique]
  );
  const resetVerificationStatus = useCallback(() => {
    dispatch(setIsEmailVerified(false));
    dispatch(setIsSmSVerified(false));
    dispatch(setOTPPopupOpen(false));
  }, [dispatch]);

  useEffect(() => {
    resetVerificationStatus();
    return () => {
      if (!isEditMode && !isViewMode) {
        dispatch(resetBMCProfileData() as any);
      }
    };
  }, [dispatch, isEditMode, isViewMode, resetVerificationStatus]);

  useEffect(() => {
    if (
      isPhoneNumberChanged &&
      !isSmSVerified &&
      ((!isEmailVerified && isEmailChanged) || !isEmailChanged)
    ) {
      dispatch(generateOTP(OTPType.SMS, BMCEditData.phoneNumber) as any);
      dispatch(setOTPPopupOpen(true) as any);
    }
  }, [isPhoneNumberChanged, isSmSVerified, isEmailVerified, dispatch]);

  useEffect(() => {
    if (isEmailChanged && !isEmailVerified && isSmSVerified) {
      dispatch(generateOTP(OTPType.Email, BMCEditData.email) as any);
      dispatch(setOTPPopupOpen(true) as any);
    }
  }, [dispatch, isEmailChanged, isEmailVerified, isSmSVerified]);

  const generateNewEmailOTP = async (e: any) => {
    e.preventDefault();
    dispatch(generateOTP(OTPType.Email, BMCEditData.email) as any);
  };

  const generateNewSMSOTP = async (e: any) => {
    e.preventDefault();
    dispatch(generateOTP(OTPType.SMS, BMCEditData.phoneNumber) as any);
  };
  useEffect(() => {
    const isVerificationCompleted =
      (isEmailChanged || isPhoneNumberChanged) &&
      isEmailVerified &&
      isSmSVerified;
    // after successful verification of email and phone number
    if (isVerificationCompleted) {
      if (isEditMode) {
        handleUpdateProfile();
      } else if (!isViewMode) {
        handleBMCRegister();
      }
    }
  }, [isEmailVerified, isSmSVerified]);
  useEffect(() => {
    // update bind data when BMCData changes to reflect on profile page
    setBMCEditData(BMCData);
  }, [BMCData]);

  const handleFileChange = (event: any) => {
    const file = event.files[0];
    if (file) {
      const fileExtension = file.name.split(".").pop();
      if (ALLOWED_FILES_EXTENSION.includes(fileExtension.toLowerCase())) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const fileObject = {
            fileName: file.name,
            fileExtension,
            fileBase64: reader.result as string,
            subject: "hi",
          };
          // fileObject.fileBase64 = fileObject.fileBase64.replace(
          //   /^data:.*;base64,/,
          //   ""
          // ); // this string makes issue on CRM while open the file
          setBMCEditData({
            ...BMCEditData,
            cDApprovedLicense: fileObject,
          }) as any;
        };
        reader.readAsDataURL(file);
      } else {
        toast.error(t("validFileExtension"));
      }
    }
  };

  const handleInputChange = (event: any): void => {
    let name, value;
    if (event?.originalEvent?.target?.inputMode === "numeric") {
      value = event.value;
      name = event.originalEvent.target.name;
    } else {
      value = event.target.value;
      name = event.target.name;
    }

    if (name === "phoneNumber" || name === "email" || name === "name") {
      setIsSubmitButtonDisabled(true);
    }
    if (value && (name === "lon" || name === "lat")) {
      value = value.toString();
    }
    setBMCEditData({
      ...BMCEditData,
      [name]: value,
    }) as any;
  };

  function updateStore() {
    dispatch(setProfileData({ ...BMCEditData }) as any);
    dispatch(setIsEditProfilePopupOpen(false) as any);
  }

  function handleUpdateProfile() {
    updateStore();
    dispatch(
      updateBMCProfile(() => {
        toast.success(t("updateProfileSuccessMessage"));
      }) as any
    );
  }
  const afterSuccessRegister = () => {
    toast.success(t("registrationSuccessMessage"));
    setTimeout(() => {
      navigate(RoutePaths.Home);
      dispatch(resetBMCProfileData() as any);
    }, DELAY_TIME);
  };
  const afterFailRegister = (res: any) => {
    setIsEmailChanged(true);
    setIsPhoneNumberChanged(true);
  };
  function handleBMCRegister() {
    updateStore();
    dispatch(createBMCProfile(afterSuccessRegister, afterFailRegister) as any);
  }

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    setIsSubmitButtonClicked(true);

    if (isViewMode || !(await validateRegistrationForm(BMCEditData))) {
      scrollUp();
      return;
    }
    resetVerificationStatus();
    if (isEditMode) {
      const emailChanged = BMCEditData.email !== BMCData.email;
      const phoneNumberChanged =
        BMCEditData.phoneNumber !== BMCData.phoneNumber;
      setIsEmailChanged(emailChanged);
      setIsPhoneNumberChanged(phoneNumberChanged);
      if (!emailChanged) {
        dispatch(setIsEmailVerified(true));
      }
      if (!phoneNumberChanged) {
        dispatch(setIsSmSVerified(true));
      }
      if (!emailChanged && !phoneNumberChanged) {
        handleUpdateProfile();
      }
    } else {
      // Handle form submission logic here
      setIsPhoneNumberChanged(true);
      setIsEmailChanged(true);
    }
  };
  const emailOtpCallbackFn = useCallback(
    (isValid: boolean) => {
      if (isValid) {
        dispatch(setIsEmailVerified(isValid) as any);
        dispatch(setOTPPopupOpen(false) as any);
      }
    },
    [dispatch]
  );

  const smsOtpCallbackFn = useCallback(
    (isValid: boolean) => {
      if (isValid) {
        dispatch(setIsSmSVerified(isValid) as any);
        dispatch(setOTPPopupOpen(false) as any);
      }
    },
    [dispatch]
  );
  function downloadCDFile(event: any): void {
    const file = BMCEditData.cDApprovedLicense;
    if (!file) return;
    const fileBase64 = file.fileBase64;
    const fileSize = calculateFileSizeFromBase64(fileBase64);
    if (fileSize > MAX_FILE_SIZE) {
      toast.error(t("fileSizeExceeds"));
      return;
    }
    const downloadLink = document.createElement("a");
    downloadLink.href = fileBase64;
    downloadLink.download = file.fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }
  const onOtpPopupClose = () => {
    setIsPhoneNumberChanged(false);
    setIsEmailChanged(false);
  };
  useEffect(() => {
    if (isSubmitButtonClicked) validateRegistrationForm(BMCEditData);
  }, [BMCEditData, isSubmitButtonClicked, validateRegistrationForm]);

  useEffect(() => {
    if (!_.isEqual(errors, registrationInitialState)) {
      validateRegistrationForm(BMCEditData);
    }
    dispatch(getCategoryOptions() as any);
  }, [language]);

  const handleTermsAndConditions = (e: any) => {
    e?.preventDefault();
    setIsTermsAndConditionsPopupOpen(true);
  };

  function getDisabledStatus(): boolean {
    if (isViewMode) return true;
    else
      return (
        (termsAndConditionsFlag && isEditMode) ||
        (!termsAndConditionsFlag && !isEditMode) ||
        isSubmitButtonDisabled ||
        !isCompanyNameUnique ||
        !isPhoneNumberUnique ||
        !isEmailUnique
      );
  }

  return (
    <>
      <div
        className={`registration-form-wrapper ps-0 ${
          isEditMode ? "edit-registration-form-wrapper" : ""
        }`}
      >
        <div
          className={`registration-form ${
            isViewMode ? "view-profile-form" : ""
          }`}
        >
          {!isViewMode}
          <form onSubmit={handleSubmit}>
            <div className="section">
              <h2> {t("Company Info")}</h2>
              <div className="form-group-row">
                <div className="form-group">
                  <label htmlFor="name" className="required">
                    {t("Company Name")}
                  </label>
                  <input
                    type="text"
                    value={BMCEditData.name}
                    id="name"
                    name="name"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className={`${
                      errors.name || !isCompanyNameUnique ? "invalid-input" : ""
                    }`}
                    onBlur={async () => {
                      if (!isNullOrWhiteSpace(BMCEditData.name)) {
                        await isCompanyNameUniqueValidation().then(() =>
                          setIsSubmitButtonDisabled(false)
                        );
                      }
                    }}
                  />
                  {errors.name && <p className="error">{errors.name}</p>}
                  {!isCompanyNameUnique && (
                    <p className="error">{t("isCompanyNameUniqueErrorMsg")}</p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="tradeLicenseNumber" className="required">
                    {t("tradeLicenseNumber")}
                  </label>
                  <input
                    type="text"
                    value={BMCEditData.tradeLicenseNumber}
                    id="tradeLicenseNumber"
                    name="tradeLicenseNumber"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className={`${
                      errors.tradeLicenseNumber ? "invalid-input" : ""
                    }`}
                  />
                  {errors.tradeLicenseNumber && (
                    <p className="error">{errors.tradeLicenseNumber}</p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="tradeLicenseExpiry" className="required">
                    {t("tradeLicenseExpiryDate")}
                  </label>
                  <Calendar
                    id="tradeLicenseExpiry"
                    value={
                      BMCEditData.tradeLicenseExpiry
                        ? convertIsoStringToDate(BMCEditData.tradeLicenseExpiry)
                        : null
                    }
                    name="tradeLicenseExpiry"
                    onChange={handleInputChange}
                    dateFormat="dd/mm/yy"
                    placeholder="DD/MM/YYYY"
                    showIcon
                    minDate={new Date()}
                    disabled={isViewMode}
                    className={`${
                      errors.tradeLicenseExpiry ? "invalid-input" : ""
                    }`}
                    ref={tradeLicenseDateRef}
                    onHide={() => onHideCalendar(tradeLicenseDateRef)}
                    onShow={() => onShowCalendar(tradeLicenseDateRef)}
                    key={`tradeLicenseExpiry-${BMCEditData.tradeLicenseExpiry?.toString()}`}
                  />
                  {errors.tradeLicenseExpiry && (
                    <p className="error">{errors.tradeLicenseExpiry}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="civilDefenseLicense" className="required">
                    {t("civilDefenseLicenseNumber")}
                  </label>
                  <input
                    type="text"
                    value={BMCEditData.civilDefenseLicense}
                    id="civilDefenseLicense"
                    name="civilDefenseLicense"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className={`${
                      errors.civilDefenseLicense ? "invalid-input" : ""
                    }`}
                  />
                  {errors.civilDefenseLicense && (
                    <p className="error">{errors.civilDefenseLicense}</p>
                  )}
                </div>
                <div className="form-group">
                  <label
                    htmlFor="civilDefenseLicenseExpiry"
                    className="required"
                  >
                    {t("civilDefenseLicenseExpiryDate")}{" "}
                  </label>
                  <Calendar
                    id="civilDefenseLicenseExpiry"
                    value={
                      BMCEditData.civilDefenseLicenseExpiry
                        ? convertIsoStringToDate(
                            BMCEditData.civilDefenseLicenseExpiry
                          )
                        : null
                    }
                    name="civilDefenseLicenseExpiry"
                    onChange={handleInputChange}
                    dateFormat="dd/mm/yy"
                    placeholder="DD/MM/YYYY"
                    showIcon
                    minDate={new Date()}
                    disabled={isViewMode}
                    className={`${
                      errors.civilDefenseLicenseExpiry ? "invalid-input" : ""
                    }`}
                    ref={civilDefenseDateRef}
                    onHide={() => onHideCalendar(civilDefenseDateRef)}
                    onShow={() => onShowCalendar(civilDefenseDateRef)}
                    key={`civilDefenseLicenseExpiry-${BMCEditData.civilDefenseLicenseExpiry?.toString()}`}
                  />
                  {errors.civilDefenseLicenseExpiry && (
                    <p className="error">{errors.civilDefenseLicenseExpiry}</p>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="categoryOfLicense" className="required">
                    {t("categoryOfLicense")}
                  </label>
                  <Dropdown
                    id="categoryOfLicense"
                    value={BMCEditData.categoryOfLicense?.toString()}
                    name="categoryOfLicense"
                    options={categoryOptions}
                    onChange={handleInputChange}
                    optionLabel={getFieldName(language)}
                    optionValue="id"
                    placeholder={t("selectCategoryPlaceHolder")}
                    disabled={isViewMode}
                    className={`${
                      errors.categoryOfLicense ? "invalid-input" : ""
                    }`}
                  />
                  {errors.categoryOfLicense && (
                    <span className="error">{errors.categoryOfLicense}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="section">
              <h2>{t("Company Address")}</h2>
              <div className="form-group-row">
                <div className="form-group view-profile-form">
                  <label className="required" htmlFor="emirate">
                    {t("emirate")}
                  </label>
                  <Dropdown
                    id="emirate"
                    value={emirateOptions[0]?.emirateId}
                    name="emirate"
                    options={emirateOptions}
                    optionLabel={getFieldName(language)}
                    optionValue="emirateId"
                    onChange={handleInputChange}
                    placeholder={t("selectAnEmirate")}
                    disabled
                    className={"disabled"}
                  />
                  {errors.emirate && (
                    <span className="error">{errors.emirate}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="officeAddress" className="required">
                    {t("officeAddress")}
                  </label>
                  <input
                    type="text"
                    value={BMCEditData.officeAddress}
                    id="officeAddress"
                    name="officeAddress"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className={`${errors.officeAddress ? "invalid-input" : ""}`}
                  />
                  {errors.officeAddress && (
                    <span className="error">{errors.officeAddress}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="street" className="required">
                    {t("street")}
                  </label>
                  <input
                    value={BMCEditData.street}
                    type="text"
                    id="street"
                    name="street"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className={`${errors.street ? "invalid-input" : ""}`}
                  />
                  {errors.street && (
                    <span className="error">{errors.street}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="city" className="required">
                    {t("City")}
                  </label>
                  <Dropdown
                    id="city"
                    value={BMCEditData.city}
                    name="city"
                    options={citiesOptions}
                    optionLabel={getFieldName(language)}
                    optionValue="cityId"
                    onChange={handleInputChange}
                    placeholder={t("selectCity")}
                    disabled={isViewMode}
                    className={`${errors.city ? "invalid-input" : ""}`}
                  />
                  {errors.city && <span className="error">{errors.city}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="lon">{t("longitude")}</label>
                  <input
                    value={BMCEditData.lon?.toString() || ""}
                    type="text"
                    id="lon"
                    name="lon"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className={`${errors.lon ? "invalid-input" : ""}`}
                  />

                  {errors.lon ? (
                    <span className="error">{errors.lon}</span>
                  ) : (
                    <p className="note">{t("longitudeNote")}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="lat">{t("latitude")}</label>
                  <input
                    value={BMCEditData.lat?.toString() || ""}
                    type="text"
                    id="lat"
                    name="lat"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className={`${errors.lat ? "invalid-input" : ""}`}
                  />
                  {errors.lat ? (
                    <span className="error">{errors.lat}</span>
                  ) : (
                    <p className="note">{t("latLongNote")}</p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="officePhoneNumber">
                    {t("Office Phone Number")}
                  </label>
                  <div className="form-group input-with-label">
                    <span className="ltr">+971</span>
                    <input
                      value={BMCEditData.officePhoneNumber}
                      type="tel"
                      id="officePhoneNumber"
                      name="officePhoneNumber"
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      className={`${
                        errors.officePhoneNumber ? "invalid-input" : ""
                      }`}
                    />
                  </div>
                  {errors.officePhoneNumber && (
                    <span className="error">{errors.officePhoneNumber}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="section">
              <h2>{t("contactPerson")}</h2>
              <div className="form-group-row">
                <div className="form-group">
                  <label htmlFor="contactPersonName" className="required">
                    {t("contactPersonName")}
                  </label>
                  <input
                    value={BMCEditData.contactPersonName}
                    type="text"
                    id="contactPersonName"
                    name="contactPersonName"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className={`${
                      errors.contactPersonName ? "invalid-input" : ""
                    }`}
                  />
                  {errors.contactPersonName && (
                    <span className="error">{errors.contactPersonName}</span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="designation">{t("designation")}</label>
                  <input
                    value={BMCEditData.designation}
                    type="text"
                    id="designation"
                    name="designation"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className={`${errors.designation ? "invalid-input" : ""}`}
                  />
                  {errors.designation && (
                    <span className="error">{errors.designation}</span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="phoneNumber" className="required">
                    {t("phoneNumber")}
                  </label>
                  <div className="form-group input-with-label">
                    <span className="ltr">+971</span>
                    <input
                      value={BMCEditData.phoneNumber}
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      className={`${
                        errors.phoneNumber || !isPhoneNumberUnique
                          ? "invalid-input"
                          : ""
                      }`}
                      onBlur={async () => {
                        if (!isNullOrWhiteSpace(BMCEditData.phoneNumber)) {
                          await isPhoneNumberUniqueValidation().then(() =>
                            setIsSubmitButtonDisabled(false)
                          );
                        }
                      }}
                    />
                  </div>
                  {errors.phoneNumber && (
                    <span className="error">{errors.phoneNumber}</span>
                  )}
                  {!isPhoneNumberUnique && (
                    <span className="error">
                      {t("isPhoneNumberUniqueErrorMsg")}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label htmlFor="email" className="required">
                    {t("Email")}
                  </label>
                  <input
                    value={BMCEditData.email}
                    type="text"
                    id="email"
                    name="email"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    className={`${
                      errors.email || !isEmailUnique ? "invalid-input" : ""
                    }`}
                    onBlur={async () => {
                      if (!isNullOrWhiteSpace(BMCEditData.email)) {
                        await isEmailUniqueValidation().then(() =>
                          setIsSubmitButtonDisabled(false)
                        );
                      }
                    }}
                  />
                  {errors.email && <p className="error">{errors.email}</p>}
                  {!isEmailUnique && (
                    <p className="ms-1 error">{t("isEmailUniqueErrorMsg")}</p>
                  )}
                </div>
                <div className="col-3 form-group">
                  <label htmlFor="numberOfTechnician">
                    {t("noOfTechnician")}
                  </label>
                  <CustomInputNumber
                    value={BMCEditData.numberOfTechnician}
                    id="numberOfTechnician"
                    name="numberOfTechnician"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    min={0}
                    className={`${
                      errors.numberOfTechnician ? "invalid-input" : ""
                    }`}
                  />
                  {errors.numberOfTechnician && (
                    <p className="error">{errors.numberOfTechnician}</p>
                  )}
                </div>
                <div className="col-3 form-group">
                  <label htmlFor="numberofElectricalTechnician">
                    {t("noOfElectricalTechnician")}
                  </label>
                  <CustomInputNumber
                    value={BMCEditData.numberofElectricalTechnician}
                    id="numberofElectricalTechnician"
                    name="numberofElectricalTechnician"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    min={0}
                    className={`${
                      errors.numberofElectricalTechnician ? "invalid-input" : ""
                    }`}
                  />
                  {errors.numberofElectricalTechnician && (
                    <p className="error">
                      {errors.numberofElectricalTechnician}
                    </p>
                  )}
                </div>
                <div className="col-3 form-group">
                  <label htmlFor="numberOfMechanicalTechnician">
                    {t("noOfMechanicalTechnician")}
                  </label>
                  <CustomInputNumber
                    value={BMCEditData.numberOfMechanicalTechnician}
                    id="numberOfMechanicalTechnician"
                    name="numberOfMechanicalTechnician"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    min={0}
                    className={`${
                      errors.numberOfMechanicalTechnician ? "invalid-input" : ""
                    }`}
                  />
                  {errors.numberOfMechanicalTechnician && (
                    <p className="error">
                      {errors.numberOfMechanicalTechnician}
                    </p>
                  )}
                </div>
                <div className="col-3 form-group">
                  <label htmlFor="numberOfCertifiedEngineers">
                    {t("noOfCertifiedEngineers")}
                  </label>
                  <CustomInputNumber
                    value={BMCEditData.numberOfCertifiedEngineers}
                    id="numberOfCertifiedEngineers"
                    name="numberOfCertifiedEngineers"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    min={0}
                    className={`${
                      errors.numberOfCertifiedEngineers ? "invalid-input" : ""
                    }`}
                  />
                  {errors.numberOfCertifiedEngineers && (
                    <p className="error">{errors.numberOfCertifiedEngineers}</p>
                  )}
                </div>
                <div className="col-3 form-group">
                  <label htmlFor="numberOfElectricalEngineer">
                    {t("noOfElectricalEngineer")}
                  </label>
                  <CustomInputNumber
                    value={BMCEditData.numberOfElectricalEngineer}
                    id="numberOfElectricalEngineer"
                    name="numberOfElectricalEngineer"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    min={0}
                    className={`${
                      errors.numberOfElectricalEngineer ? "invalid-input" : ""
                    }`}
                  />
                  {errors.numberOfElectricalEngineer && (
                    <p className="error">{errors.numberOfElectricalEngineer}</p>
                  )}
                </div>
                <div className="col-3 form-group">
                  <label htmlFor="numberOfMechanicEngineer">
                    {t("noOfMechanicalEngineer")}
                  </label>
                  <CustomInputNumber
                    value={BMCEditData.numberOfMechanicEngineer}
                    id="numberOfMechanicEngineer"
                    name="numberOfMechanicEngineer"
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    min={0}
                    className={`${
                      errors.numberOfMechanicEngineer ? "invalid-input" : ""
                    }`}
                  />
                  {errors.numberOfMechanicEngineer && (
                    <p className="error">{errors.numberOfMechanicEngineer}</p>
                  )}
                </div>
              </div>
            </div>

            <div className={`section ${isEditMode ? "m-0 pb-2" : "pb-3"}`}>
              <h2>{t("Attachments")}</h2>
              <div
                className={`form-group-row ${isViewMode ? "flex-column" : ""}`}
              >
                <label htmlFor="cDApprovedLicense" className="required">
                  {t("attachedCDApprovedLicense")}
                </label>
                {isViewMode ? (
                  <div>
                    {BMCEditData.cDApprovedLicense?.fileName || ""}
                    <Button
                      className="m-0 p-0 ms-2 download-btn"
                      text
                      onClick={downloadCDFile}
                    >
                      <FileDownloadIcon />{" "}
                    </Button>
                  </div>
                ) : (
                  <>
                    <FileUploader
                      fileUploadRef={fileUploadRef}
                      id="cDApprovedLicense"
                      name="cDApprovedLicense"
                      accept=".pdf,.jpg,.jpeg"
                      onChange={handleFileChange}
                      disabled={isViewMode}
                      className={`${
                        errors.cDApprovedLicense ? "invalid-input" : ""
                      }`}
                      fileName={BMCEditData.cDApprovedLicense?.fileName || ""}
                    />
                    <div className="file-footer d-flex flex-column w-100">
                      {errors.cDApprovedLicense && (
                        <p className="error">{errors.cDApprovedLicense}</p>
                      )}
                      <p className="note">{t("fileSizeAndTypeNote")}</p>
                    </div>
                  </>
                )}
              </div>
              {!(isViewMode || isEditMode) && (
                <div className="d-flex align-items-center gap-1">
                  <Checkbox
                    checked={termsAndConditionsFlag}
                    onChange={(e) =>
                      setTermsAndConditionsFlag(e.target.checked as boolean)
                    }
                    name="termsAndConditions"
                  ></Checkbox>
                  {t("termsAndConditionsReg")}{" "}
                  <Button
                    className="terms-and-conditions-link m-0"
                    onClick={handleTermsAndConditions}
                  >
                    {t("termsAndConditions")}
                  </Button>
                </div>
              )}
            </div>
            {!isViewMode && (
              <div
                className={`d-flex ${
                  isEditMode ? "submit-update-profile" : ""
                }`}
              >
                <Button
                  type="submit"
                  className="primary-btn submit-button border-radius-100"
                  disabled={getDisabledStatus()}
                >
                  {isEditMode ? t("update") : t("submit")}
                </Button>
              </div>
            )}
          </form>
        </div>
      </div>
      <OTP
        callbackFn={
          isPhoneNumberChanged && !isSmSVerified
            ? smsOtpCallbackFn
            : emailOtpCallbackFn
        }
        otpType={
          isPhoneNumberChanged && !isSmSVerified ? OTPType.SMS : OTPType.Email
        }
        handleRegenerateOTP={
          isPhoneNumberChanged && !isSmSVerified
            ? generateNewSMSOTP
            : generateNewEmailOTP
        }
        onOtpPopupClose={onOtpPopupClose}
        IdentifierKey={
          isPhoneNumberChanged && !isSmSVerified
            ? BMCEditData.phoneNumber
            : BMCEditData.email
        }
      />
      {isTermsAndConditionsPopupOpen && (
        <TermsAndConditionsPopup
          isTermsAndConditionsPopupOpen={isTermsAndConditionsPopupOpen}
          setIsTermsAndConditionsPopupOpen={setIsTermsAndConditionsPopupOpen}
        />
      )}
    </>
  );
};

export default RegistrationForm;
